import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LuckyWheel } from 'react-luck-draw';
import TopNavbar from './TopNavbar';
import { getWheelData } from '../api/forms';
import toastManager from '../utils/toastManager';
import './LotteryPage.css';

// 直接使用环境变量
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

export default function LotteryPage() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(null);
  const [wheelData, setWheelData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
	const [isSpinning, setIsSpinning] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [winnersList, setWinnersList] = useState([]); // 存储所有中奖记录
  const winnersListRef = useRef([]);
  const [isPrizeListUpdated, setIsPrizeListUpdated] = useState(false); // 标记奖品列表是否已更新
	const luckyRef = useRef(null);
	const prizeListRef = useRef([]);
	const [prizeIndex, setPrizeIndex] = useState(0);
  const [prizeList, setPrizeList] = useState([]);
  const [prizeInputs, setPrizeInputs] = useState([{
    id: 1,
    level: '',
    name: '',
    quantity: ''
  }]);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isPrizeInfoCollapsed, setIsPrizeInfoCollapsed] = useState(false);
  const [activeRightPanel, setActiveRightPanel] = useState('prize'); // 'prize' | 'config' | null
  const [probabilityRules, setProbabilityRules] = useState([]); // [{id, personOpenId, personName, prizeCode, prizeId, prizeName, percent}]
  const probabilityRulesRef = useRef([]);
  // 观察概率规则的变化，确保与界面一致
  useEffect(() => {
    try {
      console.log('[Lottery] probabilityRules changed:', probabilityRules);
      // 便于控制台随时查看
      if (typeof window !== 'undefined') {
        window._probabilityRules = probabilityRules;
      }
      probabilityRulesRef.current = probabilityRules;
    } catch (_) {}
  }, [probabilityRules]);
  const [newRule, setNewRule] = useState({ personOpenId: '', personName: '', prizeCode: '', prizeId: '', prizeName: '', percent: 50 });
  const [quickDrawEnabled, setQuickDrawEnabled] = useState(false); // 勾选后点击转盘将一次性抽完
  const quickDrawEnabledRef = useRef(false);
  // 同步“快速抽完”勾选到 ref，避免第三方组件缓存旧回调
  useEffect(() => {
    quickDrawEnabledRef.current = quickDrawEnabled;
  }, [quickDrawEnabled]);
  const [oneWinPerUserEnabled, setOneWinPerUserEnabled] = useState(false); // 每个用户只能中一次奖
  const oneWinPerUserEnabledRef = useRef(false);
  // 同步“每个用户只能中一次奖”到 ref
  useEffect(() => {
    oneWinPerUserEnabledRef.current = oneWinPerUserEnabled;
  }, [oneWinPerUserEnabled]);
  const [roundPrizeId, setRoundPrizeId] = useState(''); // 本轮锁定的奖品编号

  // 计算总的剩余抽奖次数
  const getTotalRemainingDraws = () => {
    return prizeList.reduce((total, prize) => {
      const quantity = parseInt(prize.quantity) || 0;
      return total + quantity;
    }, 0);
  };

  // 统一的居中弹窗：用于替代 alert
  const showCenterMessage = (title, message) => {
    setSelectedResult({ name: title || '提示', description: message || '', icon: '⚠️', showWinners: false, action: 'info' });
  };

  // 获取当前应该抽奖的奖品
  const getCurrentPrizeToDraw = () => {
    const currentPrizeList = prizeListRef.current;
    for (const prize of currentPrizeList) {
      const quantity = parseInt(prize.quantity) || 0;
      if (quantity > 0) {
        return prize;
      }
    }
    return null;
  };

  useEffect(() => {
    if (!formId) {
      navigate('/forms');
      return;
    }
    loadFormAndWheelData();
  }, [formId, navigate]);

  // 进入抽奖页时，开启非错误类Toast显示；离开时恢复默认
  useEffect(() => {
    try {
      toastManager && toastManager.setEnableNonError && toastManager.setEnableNonError(true);
    } catch (_) {}
    return () => {
      try {
        toastManager && toastManager.setEnableNonError && toastManager.setEnableNonError(false);
      } catch (_) {}
    };
  }, []);

  // 同步奖品列表到 ref
  useEffect(() => {
    prizeListRef.current = prizeList;
  }, [prizeList]);

  // 同步已中奖列表到 ref（用于“每人一次”即时排除）
  useEffect(() => {
    winnersListRef.current = winnersList;
  }, [winnersList]);

  const loadFormAndWheelData = async () => {
    try {
      setLoading(true);
			// 获取参与抽奖的人与转盘数据
			console.log('📋 表单数据请求URL:', `${API_BASE_URL}/forms/getWheelData/${formId}`);
			const res = await getWheelData(formId);
			// 兼容不同后端返回结构
			const data = res?.data ?? res;
			// form 基本信息
			const possibleForm = data?.form || data?.formData || data?.form_info || null;
			if (possibleForm) {
				setFormData(possibleForm);
			} else {
				setFormData(data || null);
			}
			// 转盘数据（奖品/参与人 + 参与人数等）
			const possibleWheel = data?.wheelData || data?.wheel || data?.lottery || null;
			let normalizedWheel = possibleWheel || {
				prizes: data?.prizes || [],
				participantCount: data?.participantCount || 0
			};
			// 如果没有奖品字段，但有参与人数组，映射为转盘项
			const participants = Array.isArray(data) ? data : (data?.participants || data?.users || data?.members || data?.list || null);
			if ((!normalizedWheel.prizes || normalizedWheel.prizes.length === 0) && Array.isArray(participants)) {
				normalizedWheel = {
					prizes: participants.map((p, idx) => ({
                  id: p?.id || `p_${idx + 1}`,
						// 显示用名称：优先微信名
						name: p?.nickName || p?.nickname || p?.name || p?.phone || p?.id || `参与者${idx + 1}`,
						// 取 openid 作为值
						value: p?.openid || p?.openID || p?.openId || p?.id,
                  // 头像 URL（用于扇区展示）
                  headImgUrl: p?.headImgUrl || p?.avatarUrl || p?.avatar
					})),
					// participantCount: participants.length
				};
			}
			setWheelData(normalizedWheel);
    } catch (error) {
      console.error('加载数据失败:', error);
      setError(error.message || '加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 统一解析参与者的 OpenId/唯一标识
  const getParticipantOpenId = (p) => {
    return p?.value || p?.openid || p?.openID || p?.openId || p?.id || '';
  };

  // 文本归一化：去首尾空格、转字符串
  const normalize = (v) => (v === undefined || v === null) ? '' : String(v).trim();

  // 统一日志输出（仅在打开调试面板时打印）
  const debugLog = (...args) => {
    console.debug('[LotteryDebug]', ...args);
  };

  // 判断某条概率规则是否命中指定奖品键
  const isRuleHitForKey = (rule, currentPrizeKey) => {
    if (!rule) return false;
    return (rule.prizeCode && String(rule.prizeCode) === String(currentPrizeKey))
      || (rule.prizeId && String(rule.prizeId) === String(currentPrizeKey))
      || (rule.prizeName && normalize(rule.prizeName) === normalize(currentPrizeKey));
  };

  // 查找强制命中的索引（概率=100%），兜底确保命中（支持 prizeId 或名称）
  const getForcedIndex = (currentPrizeKey, excludeOpenIdSet) => {
    const participants = wheelData?.prizes || [];
    for (const rule of probabilityRules) {
      if (!rule || !rule.personOpenId) continue;
      const ok = isRuleHitForKey(rule, currentPrizeKey);
      if (!ok) continue;
      const pct = Math.max(0, Math.min(100, parseFloat(rule.percent)));
      if (pct !== 100) continue;
      const idx = participants.findIndex(p => normalize(getParticipantOpenId(p)) === normalize(rule.personOpenId));
      if (idx >= 0) {
        const oid = normalize(getParticipantOpenId(participants[idx]));
        if (!excludeOpenIdSet || !oid || !excludeOpenIdSet.has(oid)) {
          debugLog('getForcedIndex: hit 100%', { idx, openId: oid, rule });
          return idx;
        }
      }
    }
    return -1;
  };

  // 计算当前奖品下每位参与者的有效概率与状态（用于调试展示）
  const computeDebugWeights = (currentPrizeKey, excludeOpenIdSet) => {
    const participants = wheelData?.prizes || [];
    const baseWeights = participants.map(() => 1);
    const forced = new Set();
    probabilityRules.forEach((rule) => {
      if (!rule || !rule.personOpenId || !rule.prizeName) return;
      const hit = (rule.prizeCode && String(rule.prizeCode) === String(currentPrizeKey))
        || (rule.prizeId && String(rule.prizeId) === String(currentPrizeKey))
        || (normalize(rule.prizeName) === normalize(currentPrizeKey));
      if (!hit) return;
      const idx = participants.findIndex(p => normalize(getParticipantOpenId(p)) === normalize(rule.personOpenId));
      if (idx >= 0) {
        const pct = Math.max(0, Math.min(100, parseFloat(rule.percent)));
        if (pct === 100) {
          baseWeights[idx] = 1e9;
          const oid = getParticipantOpenId(participants[idx]);
          if (!excludeOpenIdSet || !oid || !excludeOpenIdSet.has(oid)) forced.add(idx);
        } else if (pct > 0) {
          baseWeights[idx] = pct;
        }
      }
    });
    return participants.map((p, i) => {
      const oid = normalize(getParticipantOpenId(p));
      const excluded = !!(excludeOpenIdSet && oid && excludeOpenIdSet.has(oid));
      const weight = excluded ? 0 : baseWeights[i];
      const matchedRule = probabilityRules.find(r => (
        (r.prizeCode && String(r.prizeCode) === String(currentPrizeKey))
        || (r.prizeId && String(r.prizeId) === String(currentPrizeKey))
        || (normalize(r.prizeName) === normalize(currentPrizeKey))
      ) && normalize(r.personOpenId) === oid);
      return {
        index: i,
        name: p?.name || '',
        openId: oid,
        percent: matchedRule ? matchedRule.percent : 0,
        forced100: forced.has(i),
        excluded,
        effectiveWeight: weight
      };
    });
  };

  // 概率控制：重写版本 - 先获取概率配置，找到对应商品code的配置，再找到对应人员的openId
  const weightedRandomIndex = (currentPrizeKey, excludeOpenIdSet) => {
    const participants = wheelData?.prizes || [];
    
    console.log('[概率控制] 开始计算概率');
    console.log('[概率控制] 当前奖品Key:', currentPrizeKey);
    console.log('[概率控制] 参与者数量:', participants.length);
    
    // 1. 获取概率配置
    const allProbabilityRules = probabilityRulesRef.current || [];
    console.log('[概率控制] 所有概率配置:', allProbabilityRules);
    
    // 2. 找到对应商品code的概率配置
    const prizeRules = allProbabilityRules.filter(rule => {
      if (!rule) return false;
      
      // 匹配奖品：优先匹配code，然后匹配id，最后匹配name
      const matchCode = rule.prizeCode && String(rule.prizeCode) === String(currentPrizeKey);
      const matchId = rule.prizeId && String(rule.prizeId) === String(currentPrizeKey);
      const matchName = rule.prizeName && normalize(rule.prizeName) === normalize(currentPrizeKey);
      
      return matchCode || matchId || matchName;
    });
    
    console.log('[概率控制] 匹配当前奖品的规则数量:', prizeRules.length);
    console.log('[概率控制] 匹配的规则:', prizeRules);
    
    // 3. 将规则封装为 openId -> 概率 的对象
    const openIdProbabilityMap = {};
    prizeRules.forEach(rule => {
      if (rule.personOpenId && rule.percent !== undefined) {
        const openId = normalize(rule.personOpenId);
        // 确保概率为数值类型：先转换为数字，然后限制在0-100范围内
        const rawPercent = Number(rule.percent);
        const probability = isNaN(rawPercent) ? 1 : Math.max(0, Math.min(100, rawPercent));
        openIdProbabilityMap[openId] = probability;
        
        console.log(`[概率控制] 设置 ${openId} 的概率为 ${probability} (数值类型: ${typeof probability})`);
      }
    });
    
    console.log('[概率控制] OpenId概率映射:', openIdProbabilityMap);
    
    // 4. 为每个参与者分配权重（确保配置90%就是真正90%）
    const baseWeights = [];
    const forcedIndexes = [];
    const participantDetails = [];

    // 参与者基本信息与是否被排除
    const participantMeta = participants.map((participant, index) => {
      const openId = normalize(getParticipantOpenId(participant));
      return {
        index,
        name: participant?.name || '',
        openId,
        excluded: !!(excludeOpenIdSet && excludeOpenIdSet.has(openId))
      };
    });

    // 仅统计未排除的参与者
    const eligible = participantMeta.filter(p => !p.excluded);

    // 统计配置在未排除人群中的总和
    let configuredSum = 0;
    let configuredCount = 0;
    eligible.forEach(p => {
      if (openIdProbabilityMap.hasOwnProperty(p.openId)) {
        const v = Math.max(0, Math.min(100, Number(openIdProbabilityMap[p.openId]) || 0));
        configuredSum += v;
        configuredCount += 1;
      }
    });

    // 计算剩余百分比并平均分配给未配置的人
    const othersCount = eligible.length - configuredCount;
    const remain = Math.max(0, 100 - configuredSum);
    const avgForOthers = othersCount > 0 ? remain / othersCount : 0;

    // 写回每个人的权重（百分比即为权重）
    participantMeta.forEach(p => {
      let probability = 0;
      if (!p.excluded) {
        if (openIdProbabilityMap.hasOwnProperty(p.openId)) {
          probability = Math.max(0, Math.min(100, Number(openIdProbabilityMap[p.openId]) || 0));
        } else {
          // 未配置者按剩余均分
          probability = Number(avgForOthers) || 0;
        }
      }

      baseWeights[p.index] = probability;
      participantDetails.push({
        index: p.index,
        name: p.name,
        openId: p.openId,
        probability,
        isExcluded: p.excluded,
        isForced: probability === 100
      });

      if (probability === 100 && !p.excluded) {
        forcedIndexes.push(p.index);
        console.log(`[概率控制] ${p.name}(${p.openId}) 被设为强制中奖候选`);
      }
    });
    
    console.log('[概率控制] 参与者详情:', participantDetails);
    console.log('[概率控制] 权重数组:', baseWeights);
    console.log('[概率控制] 强制候选索引:', forcedIndexes);
    
    // 计算实际概率
    const currentTotalWeight = baseWeights.reduce((sum, weight) => sum + weight, 0);
    if (currentTotalWeight > 0) {
      console.log('[概率控制] 实际概率计算:');
      participantDetails.forEach((detail, index) => {
        const actualProbability = (detail.probability / currentTotalWeight * 100).toFixed(2);
        console.log(`  ${detail.name}(${detail.openId}): 权重${detail.probability}, 实际概率${actualProbability}%`);
      });
    }
    
    // 5. 如果有100%概率的候选，直接从中选择
    if (forcedIndexes.length > 0) {
      const selectedIndex = forcedIndexes.length === 1 ? forcedIndexes[0] : forcedIndexes[Math.floor(Math.random() * forcedIndexes.length)];
      console.log(`[概率控制] 强制中奖，选择索引: ${selectedIndex}`);
      return selectedIndex;
    }
    
    // 6. 计算总权重（确保所有权重都是数值类型）
    const totalWeight = baseWeights.reduce((sum, weight) => {
      const numWeight = Number(weight) || 0; // 确保权重为数值类型
      return sum + numWeight;
    }, 0);
    console.log('[概率控制] 总权重:', totalWeight, '(数值类型:', typeof totalWeight, ')');
    
    // 7. 如果总权重为0，随机选择
    if (totalWeight <= 0) {
      const randomIndex = Math.floor(Math.random() * participants.length);
      console.log(`[概率控制] 总权重为0，随机选择索引: ${randomIndex}`);
      return randomIndex;
    }
    
    // 8. 加权随机选择（确保所有数值计算都是数值类型）
    let randomValue = Math.random() * Number(totalWeight);
    console.log('[概率控制] 随机值:', randomValue, '(数值类型:', typeof randomValue, ')');
    
    for (let i = 0; i < baseWeights.length; i++) {
      const currentWeight = Number(baseWeights[i]) || 0; // 确保权重为数值类型
      randomValue -= currentWeight;
      console.log(`[概率控制] 减去权重[${i}]: ${currentWeight}, 剩余: ${randomValue}`);
      
      if (randomValue <= 0) {
        console.log(`[概率控制] 选中索引: ${i}, 对应参与者: ${participants[i]?.name}(${getParticipantOpenId(participants[i])})`);
        return i;
      }
    }
    
    // 兜底返回最后一个
    const lastIndex = baseWeights.length - 1;
    console.log(`[概率控制] 兜底返回索引: ${lastIndex}`);
    return lastIndex;
  };

	const startSpin = () => {

  console.log(wheelData);
  // 打印当前应抽奖品与配置信息（便于排查对齐问题）
  try {
    const currentPrize = getCurrentPrizeToDraw();
    const prizeKey = currentPrize?.code || currentPrize?.id || currentPrize?.name;
    const openIds = (wheelData?.prizes || []).map((p, i) => ({ i, name: p?.name, openId: getParticipantOpenId(p) }));
    console.log('[Lottery] 当前抽奖奖品:', currentPrize, 'key=', prizeKey);
    console.log('[Lottery] 扇区(openIds):', openIds);
    console.log('[Lottery] 概率配置(probabilityRules):', probabilityRulesRef.current);
  } catch (e) {
    console.warn('[Lottery] 打印当前奖品/配置信息失败:', e);
  }


  // if (isSpinning) return;
  // if (!wheelData || !wheelData.prizes || wheelData.prizes.length === 0) return;
  
  const currentPrize = getCurrentPrizeToDraw();
  // 开始前校验：必须存在可抽取的商品（数量>0）
  if (!currentPrize) {
    console.warn('[Lottery] 没有可抽取的奖品');
    (toastManager && toastManager.warning)
      ? toastManager.warning('请先在右侧“奖品信息”中添加并保存，且奖品数量需大于0')
      : showCenterMessage('提示', '请先在右侧“奖品信息”中添加并保存，且奖品数量需大于0');
    setIsSpinning(false);
    return;
  }

  // 锁定本轮抽奖的奖品ID，避免中途状态变化
  const lockedPrizeId = currentPrize.id;
  const lockedPrizeCode = currentPrize.code;
  const lockedPrizeName = currentPrize.name;
  setRoundPrizeId(lockedPrizeId);

  console.log('[Lottery] 开始抽奖 - 锁定奖品:', { id: lockedPrizeId, code: lockedPrizeCode, name: lockedPrizeName });

  // 打印当前应抽奖品与配置信息（便于排查对齐问题）
  try {
    const prizeKey = lockedPrizeCode || lockedPrizeId || lockedPrizeName;
    const openIds = (wheelData?.prizes || []).map((p, i) => ({ i, name: p?.name, openId: getParticipantOpenId(p) }));
    console.log('[Lottery] 当前抽奖奖品:', currentPrize, 'key=', prizeKey);
    console.log('[Lottery] 扇区(openIds):', openIds);
    console.log('[Lottery] 概率配置(probabilityRules):', probabilityRulesRef.current);
  } catch (e) {
    console.warn('[Lottery] 打印当前奖品/配置信息失败:', e);
  }

  // 快速抽奖模式：一次性抽取所有奖品（由配置信息的勾选触发）
  const quickFlag = quickDrawEnabledRef.current === true || quickDrawEnabled === true;
  console.log('[Lottery] 快速抽奖开关即时值:', { state: quickDrawEnabled, ref: quickDrawEnabledRef.current });
  if (quickFlag) {
    console.log('[Lottery] 快速抽奖模式启动');
    quickDrawAll();
    return;
  }


  // 确定排除的OpenId集合（如果启用"每个用户只能中一次奖"）
  let excludeOpenIdSet = null;
  const oneWinFlag = oneWinPerUserEnabledRef.current === true || oneWinPerUserEnabled === true;
  console.log('[Lottery] 每人一次开关:', oneWinFlag, '当前已中奖人数:', (winnersListRef.current?.length || 0));

  if (oneWinFlag) {
    // winnersList 中兼容不同字段名：winnerOpenId/openId/value
    excludeOpenIdSet = new Set(
      (winnersListRef.current || [])
        .map(w => normalize(w.winnerOpenId || w.openId || w.value))
        .filter(Boolean)
    );
    console.log('[Lottery] 每人一次开关:', oneWinFlag, '当前已中奖人数:', (winnersListRef.current?.length || 0), '排除OpenId集合:', Array.from(excludeOpenIdSet));
    // 打印已排除的候选人详细信息（nickname/name/openId/index）
    try {
      const details = (wheelData?.prizes || []).map((p, i) => ({
        index: i,
        nickname: p?.nickname,
        name: p?.name,
        openId: normalize(p?.value)
      })).filter(d => d.openId && excludeOpenIdSet.has(d.openId));
      console.log('[Lottery] 已排除候选(每人仅中一次):', details);
    } catch (_) {}
  }

  // 若所有候选人均被排除，则直接停止本次抽奖
  try {
    const participantsAll = wheelData?.prizes || [];
    const eligibleCount = (participantsAll || []).filter(p => {
      const oid = normalize(getParticipantOpenId(p));
      return !excludeOpenIdSet || !oid || !excludeOpenIdSet.has(oid);
    }).length;
    if (eligibleCount === 0) {
      console.warn('[Lottery] 无可抽取候选人：全部已被排除');
      (toastManager && toastManager.info)
        ? toastManager.info('没有可抽取的候选人（均已中奖且勾选了“每人仅中一次”）')
        : showCenterMessage('提示', '没有可抽取的候选人（均已中奖且勾选了“每人仅中一次”）。');
      setIsSpinning(false);
      return;
    }
  } catch (_) {}

  // 使用概率控制选择中奖者
  const prizeKey = lockedPrizeCode || lockedPrizeId || lockedPrizeName;
  const chooseIndex = weightedRandomIndex(prizeKey, excludeOpenIdSet);
  
  console.log('[Lottery] 概率计算完成，选择索引:', chooseIndex);
  console.log('[Lottery] 即将抽取到 ->', wheelData.prizes[chooseIndex]?.name, { 
    index: chooseIndex, 
    openId: getParticipantOpenId(wheelData.prizes[chooseIndex]),
    candidate: wheelData.prizes[chooseIndex]
  });
  try {
    const cand = wheelData.prizes[chooseIndex];
    console.log('[Lottery] 预选中奖人:', {
      nickname: cand?.nickname,
      name: cand?.name,
      openId: getParticipantOpenId(cand)
    });
  } catch (_) {}

		setIsSpinning(true);
		setSelectedResult(null);
  
		if (luckyRef.current) {
			luckyRef.current.play();
    // 延迟停止，确保转盘有足够时间旋转
			setTimeout(() => {
      if (luckyRef.current) {
        luckyRef.current.stop(chooseIndex);
      }
			}, 200);
		}
};

  // 一键快速抽奖：按当前奖品数量把所有奖品抽完，尊重概率规则
  const quickDrawAll = () => {
    const participants = wheelData?.prizes || [];
    if (participants.length === 0) return;
    let workingPrizeList = [...prizeListRef.current];
    const exclude = oneWinPerUserEnabled ? new Set(winnersList.map(w => w.winnerOpenId).filter(Boolean)) : undefined;
    const newWinners = [];
    // 循环直到没有剩余数量
    const getRemainingTotal = (list) => list.reduce((s, p) => s + (parseInt(p.quantity) || 0), 0);
    while (getRemainingTotal(workingPrizeList) > 0) {
      // 找到当前应抽的奖品（从上到下第一个剩余>0）
      let currentPrize = null;
      for (const p of workingPrizeList) {
        const qty = parseInt(p.quantity) || 0;
        if (qty > 0) { currentPrize = p; break; }
      }
      if (!currentPrize) break;
      // 选择获奖人（加权）
      const idx = weightedRandomIndex(currentPrize.name, exclude);
      const winner = participants[idx];
      const winnerName = winner?.name || `参与者${idx + 1}`;
      const winnerOpenId = winner?.value;
      console.log('[Lottery][Quick] 命中 ->', { index: idx, nickname: winner?.nickname, name: winnerName, openId: winnerOpenId });
      // 记录
      newWinners.push({
        id: Date.now() + Math.random(),
        prizeLevel: currentPrize.level,
        prizeName: currentPrize.name,
        winnerName,
        winnerOpenId,
        drawTime: new Date().toLocaleString()
      });
      if (exclude && winnerOpenId) {
        exclude.add(normalize(winnerOpenId));
        try {
          console.log('[Lottery][Quick] 新增排除用户:', {
            nickname: winner?.nickname,
            name: winnerName,
            openId: normalize(winnerOpenId)
          });
        } catch (_) {}
      }
      // 扣减数量
      workingPrizeList = workingPrizeList.map(p => p.id === currentPrize.id ? { ...p, quantity: Math.max(0, (parseInt(p.quantity) || 0) - 1) } : p);
    }
    // 一次性更新状态
    setPrizeList(workingPrizeList);
    setWinnersList(prev => [...prev, ...newWinners]);
    setSelectedResult({ name: '抽奖结束', value: 'lottery_complete', showWinners: true });
	};

  const openForm = () => {
    window.open(`/detail/${formId}`, '_blank');
  };

  const goBack = () => {
    navigate('/forms');
  };

  const handlePrizeInfoChange = (id, field, value) => {
    setPrizeInputs(prev => prev.map(input =>
      input.id === id ? { ...input, [field]: value } : input
    ));
  };

  const addPrizeRow = () => {
    const newInput = {
      id: Date.now(),
      level: '',
      name: '',
      quantity: ''
    };

    setPrizeInputs(prev => [...prev, newInput]);
  };

  const removePrizeInput = (id) => {
    if (prizeInputs.length > 1) {
      setPrizeInputs(prev => prev.filter(input => input.id !== id));
    }
  };

  const handleSavePrizeInfo = () => {
    // 将当前输入的奖品信息保存到奖品列表中
    const validInputs = prizeInputs.filter(input =>
      input.level.trim() && input.name.trim() && input.quantity.trim()
    );

    if (validInputs.length === 0) {
      toastManager && toastManager.warning ? toastManager.warning('请至少填写一个完整的奖品信息！') : showCenterMessage('提示', '请至少填写一个完整的奖品信息！');
      return;
    }

    // 将有效的奖品信息添加到奖品列表
    const newPrizes = validInputs.map(input => ({
      id: Date.now() + Math.random(),
      level: input.level,
      code: `P-${Math.random().toString(36).slice(2, 8).toUpperCase()}`,
      name: input.name,
      quantity: parseInt(input.quantity) || 0
    }));

    setPrizeList(prev => {
      const updated = [...prev, ...newPrizes];
      console.log('保存奖品信息:', { newPrizes, updated });
      return updated;
    });

    // 标记奖品列表已更新
    setIsPrizeListUpdated(true);

    // 清空输入框
    setPrizeInputs([{ id: Date.now(), level: '', name: '', quantity: '' }]);
  };

  const deletePrizeRow = (id) => {
    setPrizeList(prev => prev.filter(prize => prize.id !== id));
  };

  // 使用 useMemo 缓存转盘配置，确保样式稳定
  const wheelConfig = useMemo(() => {
    const prizes = wheelData?.prizes || [];
    const totalPrizes = prizes.length;

    return prizes.map((p, i) => {
      let topPosition = '18%';
      let fontSize = '16px';

      // 根据奖品数量调整文字位置和大小
      if (totalPrizes <= 4) {
        topPosition = '20%';
        fontSize = '18px';
      } else if (totalPrizes <= 8) {
        topPosition = '18%';
        fontSize = '16px';
      } else {
        topPosition = '15%';
        fontSize = '14px';
      }

      const item = {
        fonts: [{
          // 显示昵称为扇形文本（若无昵称，降级为 name -> openId）
          text: p.nickname || p.name || p.value || `参与者${i + 1}`,
          top: topPosition,
          fontSize: fontSize,
          fontColor: '#333'
        }],
        background: `hsl(${(i * 360 / totalPrizes)}, 70%, 85%)`,
        value: p.value
      };
      if (p.headImgUrl) {
        item.imgs = [{ src: p.headImgUrl, width: '34px', height: '34px', top: '55%' }];
      }
      return item;
    });
  }, [wheelData?.prizes]); // 只在 wheelData.prizes 变化时重新计算

  if (loading) {
    return (
      <div className="lottery-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>正在加载抽奖数据...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lottery-page">
        <div className="error-container">
          <div className="error-icon">❌</div>
          <h3>加载失败</h3>
          <p>{error}</p>
          <button onClick={goBack} className="back-btn">返回表单列表</button>
        </div>
      </div>
    );
  }

  if (!formData || !wheelData) {
    return (
      <div className="lottery-page">
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h3>数据异常</h3>
          <p>无法获取表单或转盘数据</p>
          <button onClick={goBack} className="back-btn">返回表单列表</button>
        </div>
      </div>
    );
  }

  const isAllCollapsed = isPrizeInfoCollapsed && activeRightPanel !== 'prize' && activeRightPanel !== 'config';
	return (
    <div className="lottery-page">
      <TopNavbar />
      <div className={`lottery-content ${isAllCollapsed ? 'centered' : ''}`}>
        {/* 抽奖信息卡片 */}
        <div className={`lottery-info-card ${isPrizeInfoCollapsed ? 'collapsed' : ''}`}>
          {!isPrizeInfoCollapsed && (
            <>
              <div className="prize-status">
                <h3>剩余奖品</h3>
                <div className="remaining-prizes-list">
                  {prizeList.map((prize) => {
                    const quantity = parseInt(prize.quantity) || 0;
                    if (quantity > 0) {
                      return (
                        <div key={prize.id} className="remaining-prize-item">
                          <span className="prize-level">{prize.level}</span>
                          <span className="prize-name">{prize.name}</span>
                          <span className="prize-quantity">{quantity}个</span>
                        </div>
                      );
                    }
                    return null;
                  })}
                  {prizeList.length === 0 && (
                    <div className="no-prizes">请先添加奖品信息</div>
                  )}
                  {prizeList.length > 0 && getTotalRemainingDraws() === 0 && (
                    <div className="no-prizes">暂无剩余奖品</div>
                  )}
                </div>
              </div>

              {/* 中奖人列表 */}
              {winnersList.length > 0 && (
                <div className="winners-preview">
                  <h4>中奖记录</h4>
                  <div className="winners-preview-list">
                    {winnersList.map((winner, index) => (
                      <div key={winner.id} className="winner-item">
                        <span className="winner-index">{index + 1}</span>
                        <span className="winner-prize">{winner.prizeLevel} - {winner.prizeName}</span>
                        <span className="winner-name">{winner.winnerName}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* 收缩按钮 */}
          <button
            className="collapse-btn"
            onClick={() => setIsPrizeInfoCollapsed(!isPrizeInfoCollapsed)}
            title={isPrizeInfoCollapsed ? "展开" : "收缩"}
          >
            {isPrizeInfoCollapsed ? "▶" : "◀"}
          </button>
        </div>

        {/* 抽奖转盘卡片 */}
        <div className="lottery-card">
          <div className="card-header">
        <button onClick={goBack} className="back-button">
          ← 返回
        </button>
        <div className="form-info">
					<p>参与人数: {(wheelData.prizes && wheelData.prizes.length) || wheelData.participantCount || 0} 人</p>
        </div>
      </div>
				<div className="wheel-container">
					<LuckyWheel
						ref={luckyRef}
						width="400px"
						height="400px"
						prizes={wheelConfig}
						blocks={[{ padding: '10px', background: '#fff' }]}
						buttons={[{ radius: '35%', background: '#ff6b35', pointer: true, fonts: [{ text: isSpinning ? '转盘中...' : '开始抽奖', top: '-10px', fontColor: '#fff', fontSize: '18px' }] }]}
						onStart={startSpin}
						onEnd={(prize) => {
							setIsSpinning(false);

							// 获取当前应该抽奖的奖品
							const currentPrize = getCurrentPrizeToDraw();
							if (currentPrize) {
								// 更新奖品列表中的实际数量
								setPrizeList(prev => prev.map(p =>
									p.id === currentPrize.id
										? { ...p, quantity: Math.max(0, p.quantity - 1) }
										: p
								));

								// 将中奖记录添加到列表中
                         const winnerRecord = {
									id: Date.now() + Math.random(),
									prizeLevel: currentPrize.level,
									prizeName: currentPrize.name,
                           winnerName: prize?.fonts?.[0]?.text || '',
                           winnerOpenId: prize?.value || (wheelData?.prizes?.[prizeIndex]?.value),
									drawTime: new Date().toLocaleString()
								};

								setWinnersList(prev => [...prev, winnerRecord]);

							// 打印已中奖人
							try {
								console.log('[Lottery] 本轮中奖人:', {
									nickname: prize?.fonts?.[0]?.text,
									openId: prize?.value || (wheelData?.prizes?.[prizeIndex]?.value),
									prizeLevel: currentPrize.level,
									prizeName: currentPrize.name
								});
							} catch (_) {}

								// 检查是否所有奖品都抽完了
								// 使用更新后的奖品列表计算剩余数量
								const updatedPrizeList = prizeListRef.current.map(p =>
									p.id === currentPrize.id
										? { ...p, quantity: Math.max(0, p.quantity - 1) }
										: p
								);
								const totalRemaining = updatedPrizeList.reduce((total, prize) => {
									const quantity = parseInt(prize.quantity) || 0;
									return total + quantity;
								}, 0);

								if (totalRemaining <= 0) {
									// 所有奖品抽完，显示中奖结果
									setSelectedResult({
										name: '抽奖结束',
										value: 'lottery_complete',
										showWinners: true
									});
								}
								// 如果还有奖品未抽完，不显示任何弹窗，继续抽奖
							}
						}}
					/>
          
          </div>
        </div>

        <div className={`prize-info-card ${activeRightPanel === 'prize' ? '' : 'collapsed'}`}>
          {activeRightPanel === 'prize' && (
            <>
              <div className="card-header">
                <h3>奖品信息</h3>
                <div className="header-buttons">
                  <button className="save-btn" onClick={addPrizeRow}>
                    新增行
                  </button>
                  <button className="save-btn" onClick={handleSavePrizeInfo}>
                    保存
                  </button>
                  <button
                    className="collapse-btn"
                    onClick={() => setActiveRightPanel(null)}
                    title="收缩"
                  >
                    ▶
                  </button>
                </div>
              </div>
              <div className="prize-form">
                {prizeInputs.map((input, index) => (
                <div key={input.id} className="prize-input-row">
                  <div className="row-header">
                    <span className="row-title">奖品信息 {index + 1}</span>
                    {prizeInputs.length > 1 && (
                      <button
                        className="remove-row-btn"
                        onClick={() => removePrizeInput(input.id)}
                      >
                        删除
                      </button>
                    )}
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>奖品等级</label>
                      <input
                        type="text"
                        value={input.level}
                        onChange={(e) => handlePrizeInfoChange(input.id, 'level', e.target.value)}
                        placeholder="请输入奖品等级"
                      />
                    </div>
                    {/* 奖品编号改为自动生成，不再在表单中填写 */}
                    <div className="form-group">
                      <label>奖品名称</label>
                      <input
                        type="text"
                        value={input.name}
                        onChange={(e) => handlePrizeInfoChange(input.id, 'name', e.target.value)}
                        placeholder="请输入奖品名称"
                      />
                    </div>
                    <div className="form-group">
                      <label>奖品数量</label>
                      <input
                        type="number"
                        value={input.quantity}
                        onChange={(e) => handlePrizeInfoChange(input.id, 'quantity', e.target.value)}
                        placeholder="请输入奖品数量"
                        min="1"
                      />
                    </div>
                  </div>
                </div>
                ))}
              </div>

              {/* 奖品列表 */}
              {prizeList.length > 0 && (
                <div className="prize-list">
                  <h4>已添加的奖品</h4>
                    <div className="prize-table">
                      <div className="table-header" style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr 80px' }}>
                        <div>奖品等级</div>
                        <div>奖品编号</div>
                        <div>奖品名称</div>
                        <div>奖品数量</div>
                        <div>操作</div>
                      </div>
                    {prizeList.map((prize) => (
                      <div key={prize.id} className="table-row" style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr 80px' }}>
                        <div>{prize.level}</div>
                        <div>{prize.code || ''}</div>
                        <div>{prize.name}</div>
                        <div>{prize.quantity}</div>
                        <div>
                          <button
                            className="delete-btn"
                            onClick={() => deletePrizeRow(prize.id)}
                          >
                            删除
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* 收缩状态下的箭头按钮 */}
          {activeRightPanel !== 'prize' && (
            <button
              className="expand-arrow"
              onClick={() => setActiveRightPanel('prize')}
              title="展开奖品信息"
            >
              ◀
            </button>
          )}
        </div>

        {/* 配置信息卡片 */}
        <div className={`additional-card ${activeRightPanel === 'config' ? '' : 'collapsed'}`}>
          {activeRightPanel === 'config' && (
            <>
              <div className="card-header">
                <h3>配置信息</h3>
                <div className="header-buttons">
                  <button
                    className="collapse-btn"
                    onClick={() => setActiveRightPanel(null)}
                    title="收缩"
                  >
                    ▶
                  </button>
                </div>
              </div>
              <div className="card-content">
                {/* 概率控制 */}
                <div className="config-section">
                  <h4>概率控制</h4>
                  <div className="form-row">
                  <div className="form-group">
                    <label>人名</label>
                    <select
                      value={newRule.personOpenId}
                      onChange={(e) => {
                        const oid = e.target.value;
                        const p = (wheelData?.prizes || []).find(x => x.value === oid);
                        setNewRule(r => ({ ...r, personOpenId: oid, personName: p?.name || '' }));
                      }}
                    >
                      <option value="">请选择参与者</option>
                      {(wheelData?.prizes || []).map(p => (
                        <option key={p.value} value={p.value}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>奖品编号</label>
                    <select
                      value={newRule.prizeCode}
                      onChange={(e) => {
                        const code = e.target.value;
                        const it = prizeList.find(x => String(x.code) === String(code));
                        setNewRule(r => ({ ...r, prizeCode: code, prizeId: it?.id || '', prizeName: it?.name || '' }));
                      }}
                    >
                      <option value="">请选择奖品编号</option>
                      {prizeList.map(p => (
                        <option key={p.id} value={p.code}>{p.code} - {p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>概率(%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={newRule.percent}
                      onChange={(e) => setNewRule(r => ({ ...r, percent: e.target.value }))}
                      placeholder="0-100，100%必中"
                    />
                  </div>
                    <div className="form-group">
                  <button
                    className="save-btn"
                    onClick={() => {
                      // 校验必须项：人(OpenId)、奖品编号、概率
                      const oid = (newRule.personOpenId || '').trim();
                      const code = (newRule.prizeCode || '').trim();
                      const pctNum = Number(newRule.percent);
                      if (!oid || !code || isNaN(pctNum)) {
                        toastManager && toastManager.error ? toastManager.error('请完整填写：人名、奖品编号、概率(%)') : showCenterMessage('提示', '请完整填写：人名、奖品编号、概率(%)');
                        return;
                      }
                      const percent = Math.max(0, Math.min(100, pctNum));
                      const rule = { id: Date.now(), ...newRule, percent };
                      setProbabilityRules(prev => [...prev, rule]);
                      setNewRule({ personOpenId: '', personName: '', prizeCode: '', prizeId: '', prizeName: '', percent: 50 });
                    }}
                  >
                    添加规则
                  </button>
                    </div>
                </div>
                {probabilityRules.length > 0 && (
                  <div className="prize-list" style={{ marginTop: 10 }}>
                    <h4>概率规则</h4>
                    <div className="prize-table">
                      <div className="table-header" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr 1fr 80px' }}>
                        <div>人名</div>
                        <div>OpenId</div>
                        <div>奖品编号</div>
                        <div>概率(%)</div>
                        <div>操作</div>
                      </div>
                      {probabilityRules.map(rule => (
                        <div key={rule.id} className="table-row" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr 1fr 80px' }}>
                          <div>{rule.personName || rule.person || '-'}</div>
                          <div style={{wordBreak:'break-all'}}>{rule.personOpenId || '-'}</div>
                          <div>{rule.prizeCode || '-'}</div>
                          <div>{rule.percent}</div>
                          <div>
                            <button className="delete-btn" onClick={() => setProbabilityRules(prev => prev.filter(r => r.id !== rule.id))}>删除</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* 结束 权重控制 section */}
                </div>

                {/* 其他配置 */}
                <div className="config-section" style={{ marginTop: 20 }}>
                  <h4>其他配置</h4>
                  <label className="checkbox-inline">
                    <input
                      type="checkbox"
                      checked={quickDrawEnabled}
                      onChange={(e) => setQuickDrawEnabled(e.target.checked)}
                    />
                    <span>快速抽完（勾选后点击转盘一次性抽完）</span>
                  </label>
                  <label className="checkbox-inline" style={{ marginLeft: 16 }}>
                    <input
                      type="checkbox"
                      checked={oneWinPerUserEnabled}
                      onChange={(e) => setOneWinPerUserEnabled(e.target.checked)}
                    />
                    <span>每个用户只能中一次奖</span>
                  </label>
                </div>
              </div>
            </>
          )}
          {activeRightPanel !== 'config' && (
            <button
              className="expand-arrow"
              onClick={() => setActiveRightPanel('config')}
              title="展开配置信息"
            >
              ◀
            </button>
          )}
				</div>

				{/* 奖品列表移除：当前参与人即扇区名称，不再单独展示 */}

        {/* 居中弹窗：用于结果与提示信息 */}
        {selectedResult && (
          <div
            className="modal-overlay"
            onClick={() => setSelectedResult(null)}
            style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <div className="modal-content" style={{ position: 'relative', margin: 0 }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <button
                  className="modal-close"
                  onClick={() => setSelectedResult(null)}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                {selectedResult.showWinners ? (
                  <div className="winners-list">
                    <div className="result-icon">🏆</div>
                    <h3>抽奖结束</h3>
                    <h4>中奖名单</h4>
                    <div className="winners-table">
                      <div className="table-header">
                        <div>奖品等级</div>
                        <div>奖品名称</div>
                        <div>获奖人</div>
                        <div>抽奖时间</div>
                      </div>
                      {winnersList.map((winner) => (
                        <div key={winner.id} className="table-row">
                          <div>{winner.prizeLevel}</div>
                          <div>{winner.prizeName}</div>
                          <div>{winner.winnerName}</div>
                          <div>{winner.drawTime}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
              <div className="result-icon">
                {selectedResult.icon || '🎉'}
              </div>
              <h3>恭喜您！</h3>
              <h4>{selectedResult.name}</h4>
                    {selectedResult.prizeInfo && (
                      <div className="prize-details">
                        <p><strong>奖品等级：</strong>{selectedResult.prizeInfo.level}</p>
                        <p><strong>奖品名称：</strong>{selectedResult.prizeInfo.name}</p>
                      </div>
                    )}
              {selectedResult.description && (
                <p className="result-description">{selectedResult.description}</p>
              )}
                  </>
                )}
              </div>
              {!selectedResult.showWinners && selectedResult.action === 'info' && (
                <div className="modal-footer">
                <button 
                  className="spin-again-btn"
                  onClick={() => setSelectedResult(null)}
                >
                    知道了
                </button>
              </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
